import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Autosuggest from 'react-autosuggest';
import { connect } from 'react-redux';

import Button from 'src/components/buttons/button';
import { performSearch } from 'src/utils/router';
import api from 'src/utils/api';
import i18n from 'src/utils/translate';
import { throttle } from 'lodash';
import { resetQuery } from 'src/modules/search/shared/actions';

class SearchBar extends Component {
  static propTypes = {
    query: PropTypes.string.isRequired,
    resetQuery: PropTypes.func.isRequired,
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    currentCommunity: PropTypes.object,
    disableAutosuggest: PropTypes.bool,
    disableQueryPlaceholder: PropTypes.bool,
    isCommunitySearch: PropTypes.bool,
    placeholderText: PropTypes.string,
    reloadPage: PropTypes.bool,
    searchType: PropTypes.string,
    onClick: PropTypes.func,
    onSearch: PropTypes.func,
  }

  static defaultProps = {
    autoFocus: false,
    className: null,
    currentCommunity: {},
    disableAutosuggest: false,
    disableQueryPlaceholder: false,
    isCommunitySearch: false,
    placeholderText: '',
    reloadPage: false,
    searchType: 'posts',
    onClick: () => {},
    onSearch: () => {},
  }

  constructor(props) {
    super(props);
    this.state = {
      focus: false,
      query: '',
      suggestions: [],
    };
  }

  componentDidMount() {
    this.setState({
      query: this.props.query,
      focus: this.props.autoFocus && !this.props.query,
    });
    if (this.input && this.props.autoFocus && !this.props.query) {
      this.input.focus();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.query !== nextProps.query) {
      this.setState({ query: nextProps.query });
    }
  }

  componentWillUnmount() {
    this.props.resetQuery();
  }

  onFocus = () => {
    this.setState({ focus: true });
  }

  onBlur = () => {
    this.setState({ focus: false });
  }

  onClick = () => {
    this.props.onClick();
  }

  handleSubmit = (e) => {
    document.activeElement.blur();
    if (e) e.preventDefault();

    const { currentCommunity, isCommunitySearch, searchType, reloadPage } = this.props;
    const communityId = isCommunitySearch ? currentCommunity.communityId : 'all';
    const communitySlug = isCommunitySearch ? currentCommunity.slug : null;

    performSearch(this.state.query, communityId, communitySlug, reloadPage, searchType);
    this.props.onSearch();
    this.setState({ focus: false });
  }

  onSuggestionsFetchRequested = throttle(async ({ value = '' }) => {
    if (value.length <= 1) {
      return;
    }
    const { data } = await api.getPublic(`search/autocomplete?q=${value}`);
    this.setState({
      // TODO: remove toLowerCase when the b/e is fixed
      suggestions: data.slice(0, 10).map(x => x.toLowerCase()),
    });
  }, 500);

  onSuggestionsClearRequested = () => {
    this.setState({ suggestions: [] });
  }

  onSuggestionSelected = (e, { suggestion }) => {
    this.setState({ query: suggestion }, () => {
      this.handleSubmit();
    });
  }

  renderPlaceholder = () => {
    const { query, placeholderText, disableQueryPlaceholder } = this.props;
    if (disableQueryPlaceholder) return placeholderText;
    return (!!placeholderText && !query) ? placeholderText : '';
  }

  renderInputComponent = (props) => {
    const inputContainerClassName = classNames({
      'search-bar__container': true,
      'search-bar__container--focused': this.state.focus,
    });
    const customProps = {
      ...props,
      'aria-label': i18n.t('Start a search'),
      ref: (node) => {
        this.input = node;
        props.ref(node);
      },
    };
    return (
      <label className={inputContainerClassName}>
        <input {...customProps} />
        <Button
          className="search-bar__icon solo-icon"
          disabled={!this.state.query}
          icon="search-1"
          isSubmit={true}
          text={<span className="search-bar__submit-text">{ i18n.t('Search') }</span>}
        />
      </label>
    );
  }

  renderInputWithAutosuggestDisabled = () => {
    const inputContainerClassName = classNames({
      'search-bar__container': true,
      'search-bar__container--focused': this.state.focus,
    });

    return (
      <label className={inputContainerClassName}>
        <input
          aria-label={i18n.t('Start a search')}
          autoFocus={true}
          className="search-bar__input"
          placeholder={this.renderPlaceholder()}
          type="text"
          value={this.state.query}
          onBlur={this.onBlur}
          onChange={e => this.setState({ query: e.target.value })}
          onFocus={this.onFocus}
        />
        <Button
          className="search-bar__icon solo-icon"
          disabled={!this.state.query}
          icon="search-1"
          isSubmit={true}
          text={<span className="search-bar__submit-text">{ i18n.t('Search') }</span>}
        />
      </label>
    );
  }

  renderSuggestion = (text) => {
    const highlight = this.state.query;
    // queries with special characters (e.g. 'Pregnancy (difficulties)') were breaking the RegExp so they are now escaped
    const escapedHighlight = highlight.replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedHighlight})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) =>
          <span
            key={i}
            style={part.toLowerCase() === highlight.toLowerCase() ? { fontWeight: 'bold' } : {}}
          >
            {part}
          </span>)}
      </span>
    );
  }

  render() {
    const containerClassName = classNames({
      'search-bar': true,
      [this.props.className]: !!this.props.className,
    });
    const inputProps = {
      className: 'search-bar__input',
      placeholder: this.renderPlaceholder(),
      onChange: (e, { newValue }) => {
        this.setState({ query: newValue });
      },
      value: this.state.query,
      type: 'text',
      onBlur: this.onBlur,
      onClick: this.onClick,
      onFocus: this.onFocus,
    };

    return (
      <div className={containerClassName}>
        <form
          className="search-bar__form"
          onSubmit={this.handleSubmit}
        >
          { this.props.disableAutosuggest
            ?
              this.renderInputWithAutosuggestDisabled()
            :
              <Autosuggest
                getSuggestionValue={suggestion => suggestion}
                id={this.props.customId || 'search-bar'}
                inputProps={inputProps}
                renderInputComponent={this.renderInputComponent}
                renderSuggestion={this.renderSuggestion}
                suggestions={this.state.suggestions}
                title="search-bar"
                onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                onSuggestionSelected={this.onSuggestionSelected}
                onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
              />
          }
        </form>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { pages: { search = {} }, context } = state;
  return {
    query: search.searchQuery || '',
    currentCommunity: context.currentCommunity,
  };
};

export default connect(mapStateToProps, { resetQuery })(SearchBar);
